import type { LocalizedString, RiskModel } from "@repo/shared/types";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { AlertMessage } from "@/components/ui/alert-message";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { LocalizedInput } from "@/components/ui/localized-input";
import { ApiError } from "@/lib/api/_client";
import { useCloneRiskModel } from "@/lib/hooks/use-risk-models";
import { useUserLanguage } from "@/lib/i18n/context";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./clone-risk-model-dialog.i18n";

const COPY_SUFFIX = " (copy)";

/** Mark the copy as a copy in every language the source name carries. */
const suffixEveryLocale = (nameI18n: LocalizedString): LocalizedString =>
  Object.fromEntries(
    Object.entries(nameI18n ?? {}).map(([locale, value]) => [locale, `${value}${COPY_SUFFIX}`]),
  );

type CloneRiskModelDialogProps = {
  open: boolean;
  model: RiskModel | null;
  onClose: () => void;
};

export function CloneRiskModelDialog({ open, model, onClose }: CloneRiskModelDialogProps) {
  const navigate = useNavigate();
  const { userLanguage, tenantSupportedLanguages } = useUserLanguage();
  const localize = useLocalize();
  const t = useT(T);
  const cloneModel = useCloneRiskModel();

  const [name, setName] = useState("");
  const [nameI18n, setNameI18n] = useState<LocalizedString>({});
  const [description, setDescription] = useState("");
  const [descriptionI18n, setDescriptionI18n] = useState<LocalizedString>({});
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (model) {
      const suffixed = suffixEveryLocale(model.nameI18n);
      setNameI18n(suffixed);
      setName(localize(suffixed) || `${localize(model.nameI18n)}${COPY_SUFFIX}`);
      setDescriptionI18n(model.descriptionI18n ?? {});
      setDescription(model.descriptionI18n ? localize(model.descriptionI18n) : "");
    }
    setErrorMessage(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model, open]);

  // Closing mid-clone would hide the result: the navigation to the copy and any
  // failure alert both land on a dialog the user has already dismissed.
  const blockDismissWhilePending = (event: { preventDefault: () => void }) => {
    if (cloneModel.isPending) event.preventDefault();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!model || !name.trim()) return;
    setErrorMessage(null);

    const fullNameI18n: LocalizedString = { ...nameI18n, [userLanguage]: name };
    const fullDescriptionI18n: LocalizedString | undefined = description.trim()
      ? { ...descriptionI18n, [userLanguage]: description }
      : undefined;

    try {
      const created = await cloneModel.mutateAsync({
        id: model.id,
        data: { nameI18n: fullNameI18n, descriptionI18n: fullDescriptionI18n },
      });
      onClose();
      navigate(`/settings/risk-models/${created.id}`);
    } catch (err) {
      setErrorMessage(err instanceof ApiError ? err.message : t.failedToDuplicate);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!next && !cloneModel.isPending) onClose();
        }}
      >
        <DialogContent
          className="sm:max-w-[600px]"
          onEscapeKeyDown={blockDismissWhilePending}
          onInteractOutside={blockDismissWhilePending}
        >
          <DialogHeader>
            <DialogTitle>{t.title}</DialogTitle>
            <DialogDescription>{t.description}</DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <LocalizedInput
              id="clone-risk-model-name"
              label={t.name}
              value={name}
              i18nValue={nameI18n}
              onChange={setName}
              onI18nChange={setNameI18n}
              supportedLanguages={tenantSupportedLanguages}
              required
            />

            <LocalizedInput
              id="clone-risk-model-description"
              label={t.modelDescription}
              value={description}
              i18nValue={descriptionI18n}
              onChange={setDescription}
              onI18nChange={setDescriptionI18n}
              supportedLanguages={tenantSupportedLanguages}
              placeholder={t.descriptionPlaceholder}
              multiline
              rows={2}
            />

            <DialogFooter>
              <DialogCancel disabled={cloneModel.isPending}>{t.cancel}</DialogCancel>
              <Button type="submit" disabled={!name.trim()} loading={cloneModel.isPending}>
                {t.duplicate}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertMessage
        open={errorMessage !== null}
        onOpenChange={(next) => {
          if (!next) setErrorMessage(null);
        }}
        title={t.error}
        description={errorMessage ?? ""}
        variant="destructive"
      />
    </>
  );
}
